import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Reveal from '../Components/Reveal';
import '../styles/Contact.css';

const CATEGORIES = ['Digital Twins', 'AI & ML', 'Extended Reality', 'Custom Development', 'Others'];
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:4000';

const Contact = () => {
  const navigate = useNavigate();
  const [activeCategory, setActiveCategory] = useState('Digital Twins');
  const [form, setForm] = useState({ firstName: '', lastName: '', email: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${BACKEND_URL}/api/contact/digital`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: `${form.firstName} ${form.lastName}`.trim(),
          email: form.email,
          category: activeCategory,
          message: form.message,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Something went wrong.');
      setSubmitted(true);
      setForm({ firstName: '', lastName: '', email: '', message: '' });
    } catch (err) {
      setError(err.message || 'Failed to send. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="dw-contact">

      {/* ── HERO ───────────────────────────────────────────── */}
      <header className="dw-contact__hero-wrap">
        <div className="dw-contact__hero">
          <Reveal animation="fade-up">
            <div className="dw-contact__hero-content">
              <span className="dw-contact__hero-eyebrow">Start a Conversation</span>
              <h1 className="dw-contact__hero-heading">Connect With Us</h1>
              <p className="dw-contact__hero-subtitle">Let's Build the Future Together</p>
              <p className="dw-contact__hero-sub">
                Ready to transform your operations? Our team is here to help you bridge the digital gap.
              </p>
            </div>
          </Reveal>
        </div>
      </header>

      {/* ── MAIN CONTACT SECTION ──────────────────────────── */}
      <section className="dw-contact__main">
        <div className="dw-contact__main-inner">

          {/* Left: contact details */}
          <Reveal animation="fade-right" className="dw-contact__details" style={{ clipPath: 'none', overflow: 'visible' }}>
            <h2 className="dw-contact__details-heading">We're here to help.</h2>
            <p className="dw-contact__details-sub">
              Whether you have questions about our AI solutions, want a demo of our XR platforms, or need a custom system development consultation—let's build the future together.
            </p>

            <div className="dw-contact__info-list">
              <div className="dw-contact__info-row">
                <div className="dw-contact__info-icon dw-contact__info-icon--purple">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z"/><circle cx="12" cy="10" r="3"/></svg>
                </div>
                <div>
                  <p className="dw-contact__info-label">Headquarters (Malaysia)</p>
                  <p className="dw-contact__info-value">
                    Suite 12, WORQ Sunway Putra,<br />
                    Level 4-1, Sunway Putra Mall,<br />
                    100, Jalan Putra, Chow Kit,<br />
                    50350 Kuala Lumpur,<br />
                    W.P. Kuala Lumpur, Malaysia
                  </p>
                </div>
              </div>

              <div className="dw-contact__info-row">
                <div className="dw-contact__info-icon dw-contact__info-icon--emerald">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z"/><circle cx="12" cy="10" r="3"/></svg>
                </div>
                <div>
                  <p className="dw-contact__info-label">Regional Office (Australia)</p>
                  <p className="dw-contact__info-value">
                    40/57 Frederick Street,<br />
                    Belmont, 6104,<br />
                    Western Australia, Australia
                  </p>
                </div>
              </div>

              <div className="dw-contact__info-row">
                <div className="dw-contact__info-icon dw-contact__info-icon--blue">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="20" height="16" x="2" y="4" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></svg>
                </div>
                <div>
                  <p className="dw-contact__info-label">Inquiries</p>
                  <p className="dw-contact__info-value">
                    connect@teczo.co<br />
                    Response time: Within 24 hours
                  </p>
                </div>
              </div>
            </div>

            {/* Social pills */}
            <div className="dw-contact__socials">
              <a href="https://www.linkedin.com/company/teczo-sdn-bhd" target="_blank" rel="noreferrer" className="dw-contact__social-pill">
                <svg width="16" height="16" fill="currentColor" viewBox="0 0 24 24"><path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/></svg>
                LinkedIn
              </a>
            </div>
          </Reveal>

          {/* Right: form */}
          <Reveal animation="fade-left" delay={120} className="dw-contact__form-wrap" style={{ clipPath: 'none', overflow: 'visible' }}>
            <div className="dw-contact__form-card">
              {submitted ? (
                <div className="dw-contact__success">
                  <div className="dw-contact__success-icon">
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#2AAECC" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5"/></svg>
                  </div>
                  <h3 className="dw-contact__success-title">Inquiry sent!</h3>
                  <p className="dw-contact__success-body">Our team will get back to you shortly.</p>
                  <button className="dw-contact__success-back" onClick={() => setSubmitted(false)}>Send another message</button>
                </div>
              ) : (
                <form onSubmit={handleSubmit}>
                  {/* Category pills */}
                  <div className="dw-contact__cat-section">
                    <p className="dw-contact__cat-label">What are you interested in?</p>
                    <div className="dw-contact__cat-pills">
                      {CATEGORIES.map(cat => (
                        <button
                          key={cat}
                          type="button"
                          className={`dw-contact__cat-pill ${activeCategory === cat ? 'dw-contact__cat-pill--active' : ''}`}
                          onClick={() => setActiveCategory(cat)}
                        >
                          {cat}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Name row */}
                  <div className="dw-contact__form-row">
                    <div className="dw-contact__form-field">
                      <label className="dw-contact__form-label">First Name</label>
                      <input
                        name="firstName"
                        type="text"
                        placeholder="John"
                        required
                        className="dw-contact__form-input"
                        value={form.firstName}
                        onChange={handleChange}
                      />
                    </div>
                    <div className="dw-contact__form-field">
                      <label className="dw-contact__form-label">Last Name</label>
                      <input
                        name="lastName"
                        type="text"
                        placeholder="Doe"
                        required
                        className="dw-contact__form-input"
                        value={form.lastName}
                        onChange={handleChange}
                      />
                    </div>
                  </div>

                  {/* Email */}
                  <div className="dw-contact__form-field">
                    <label className="dw-contact__form-label">Work Email</label>
                    <input
                      name="email"
                      type="email"
                      placeholder="john@company.com"
                      required
                      className="dw-contact__form-input"
                      value={form.email}
                      onChange={handleChange}
                    />
                  </div>

                  {/* Message */}
                  <div className="dw-contact__form-field">
                    <label className="dw-contact__form-label">Your Message</label>
                    <textarea
                      name="message"
                      rows={5}
                      placeholder="Tell us about your project..."
                      required
                      className="dw-contact__form-textarea"
                      value={form.message}
                      onChange={handleChange}
                    />
                  </div>

                  {error && <p style={{ color: '#ef4444', fontSize: '0.875rem', fontWeight: 600, marginBottom: '0.5rem' }}>{error}</p>}

                  <button type="submit" className="dw-contact__form-submit" disabled={loading}>
                    {loading ? 'Sending...' : 'Send Inquiry'}
                  </button>

                  <p className="dw-contact__form-privacy">
                    By sending this, you agree to our <a href="#0">Privacy Policy</a>.
                  </p>
                </form>
              )}
            </div>
          </Reveal>

        </div>
      </section>

      {/* ── FOOTER CTA ────────────────────────────────────── */}
      <footer className="dw-contact__footer-wrap">
        <div className="dw-contact__footer-cta">
          <Reveal animation="fade-up">
            <div className="dw-contact__footer-inner">
              <h2 className="dw-contact__footer-heading">Build the Future.</h2>
              <p className="dw-contact__footer-sub">
                From AI systems to XR-powered digital twins, DigitalWorks delivers the technology foundation for next-generation operations.
              </p>
              <button className="dw-contact__footer-btn" onClick={() => navigate('/contact')}>Contact Us</button>
            </div>
          </Reveal>
        </div>
      </footer>

    </main>
  );
};

export default Contact;
